import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Search, Settings } from 'lucide-react';
import { Skill } from '../../types';
import { localized } from '../../utils';

interface Props {
  skills: Skill[];
  selected: Skill[];
  onToggle: (skill: Skill) => void;
  onManage: () => void;
}

const inputCls = "glass rounded-lg w-full border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 !pl-9 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition text-sm";
const iconBtn = 'p-2.5 rounded-lg border border-ink/[0.08] text-zinc-500 hover:text-primary-light hover:border-primary/30 hover:bg-primary/5 transition-all flex-shrink-0';
// Known categories first (key = French name, stable across languages)
const CATEGORY_ORDER = ['Langage', 'Technologie', 'Compétence', 'Soft skill'];

/** Skill catalogue grouped by collapsible category, with search; click a pill to add or remove. */
export default function SkillPicker({ skills, selected, onToggle, onManage }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const groups = Object.entries(skills.reduce<Record<string, Skill[]>>((acc, sk) => {
    (acc[sk.category.fr] = acc[sk.category.fr] || []).push(sk);
    return acc;
  }, {}))
    .sort(([a], [b]) => (CATEGORY_ORDER.indexOf(a) + 1 || 99) - (CATEGORY_ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b))
    .map(([key, list]) => [key, [...list].sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang))] as const);
  const needle = query.trim().toLowerCase();

  return (
    <>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input type="search" className={inputCls} placeholder={t('search') as string} aria-label={t('search') as string}
            value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <button type="button" onClick={onManage} className={iconBtn} aria-label="Manage skills"><Settings size={16} /></button>
      </div>
      <p className="text-xs text-zinc-500">{t('skills_hint')}</p>
      {groups.map(([key, list]) => {
        const shown = needle ? list.filter(sk => localized(sk.name, lang).toLowerCase().includes(needle)) : list;
        if (!shown.length) return null;
        const picked = list.filter(sk => selected.some(e => e.id === sk.id));
        const isOpen = !!needle || !!open[key];
        return (
          <div key={key} className="border border-line rounded-xl overflow-hidden">
            <button type="button" aria-expanded={isOpen} onClick={() => setOpen(prev => ({ ...prev, [key]: !prev[key] }))}
              className="flex items-center gap-3 w-full min-h-[52px] px-4 bg-surface text-left text-sm font-semibold text-ink">
              <ChevronDown size={18} className={`text-zinc-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              {localized(list[0].category, lang)}
              <span className="text-xs font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">{picked.length} / {list.length}</span>
              {!isOpen && <span className="flex-1 min-w-0 truncate text-right text-[13px] font-normal text-zinc-500">{picked.map(sk => localized(sk.name, lang)).join(', ')}</span>}
            </button>
            {isOpen && (
              <div className="p-4 flex flex-wrap gap-2 border-t border-line">
                {shown.map(sk => {
                  const on = selected.some(e => e.id === sk.id);
                  return (
                    <button key={sk.id} type="button" aria-pressed={on} onClick={() => onToggle(sk)}
                      className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-medium border transition-colors ${on ? 'bg-primary text-white border-primary' : 'border-primary/30 text-ink hover:bg-surface'}`}>
                      {on && <Check size={13} />}{localized(sk.name, lang)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
